import type {
  SensitiveApprovalResponse,
  SensitiveApprovalStatusValue,
  SensitiveApprovalWebsocketPayload,
} from './types'
import { formatApprovalTarget } from './approvalBusiness'
import { SensitiveApprovalPhase, SensitiveApprovalStatus } from './types'

export const SENSITIVE_APPROVAL_CHANGED_EVENT = 'sensitive-approval:changed'

export interface SensitiveApprovalPendingTicket extends SensitiveApprovalWebsocketPayload {
  key?: string
  ticketId?: number
  requestId?: string
  status?: SensitiveApprovalStatusValue | string
  createdAt: number
  updatedAt: number
}

export type SensitiveApprovalBusinessTarget = Pick<SensitiveApprovalResponse, 'businessType' | 'businessId'>

const BLOCKING_STATUSES = new Set<string>([
  SensitiveApprovalStatus.Pending,
  SensitiveApprovalStatus.Approved,
  SensitiveApprovalStatus.Executing,
])

const TERMINAL_STATUSES = new Set<string>([
  SensitiveApprovalStatus.Executed,
  SensitiveApprovalStatus.Rejected,
  SensitiveApprovalStatus.Canceled,
  SensitiveApprovalStatus.Voided,
  SensitiveApprovalStatus.Expired,
  SensitiveApprovalStatus.ExecuteFailed,
])

export function buildApprovalBusinessKey(target: SensitiveApprovalBusinessTarget) {
  if (!target.businessType || !target.businessId) {
    return undefined
  }
  return `${target.businessType}:${target.businessId}`
}

export function isApprovalBlockingStatus(status?: string) {
  return Boolean(status && BLOCKING_STATUSES.has(status))
}

export function isApprovalTerminalStatus(status?: string) {
  return Boolean(status && TERMINAL_STATUSES.has(status))
}

export function shouldOpenExistingApprovalReview(
  payload: Pick<SensitiveApprovalResponse, 'existingTicket' | 'canAudit' | 'ticketId'> | undefined | null,
) {
  return Boolean(
    payload?.existingTicket === true
    && payload.canAudit === true
    && typeof payload.ticketId === 'number'
    && Number.isSafeInteger(payload.ticketId)
    && payload.ticketId > 0,
  )
}

export function shouldRefreshTableAfterSensitiveApproval(
  payload: Pick<SensitiveApprovalResponse | SensitiveApprovalWebsocketPayload, 'businessType' | 'status'> | undefined | null,
) {
  return payload?.status === SensitiveApprovalStatus.Executed
    && (payload.businessType === 'TABLE_RECORD' || payload.businessType === 'ITEM_ORDER')
}

export function isExecutedItemOrderApproval(
  payload: Pick<SensitiveApprovalResponse | SensitiveApprovalWebsocketPayload, 'businessType' | 'businessId' | 'status'> | undefined | null,
  itemOrderIds: Array<number | string | null | undefined>,
) {
  if (!payload || payload.status !== SensitiveApprovalStatus.Executed || payload.businessType !== 'ITEM_ORDER') {
    return false
  }
  const businessId = payload.businessId === undefined || payload.businessId === null ? undefined : String(payload.businessId)
  if (!businessId) {
    return false
  }
  return itemOrderIds.some(itemOrderId => itemOrderId !== undefined && itemOrderId !== null && String(itemOrderId) === businessId)
}

export function normalizeApprovalPendingTicket(
  payload: SensitiveApprovalResponse | SensitiveApprovalWebsocketPayload,
  now = Date.now(),
): SensitiveApprovalPendingTicket | null {
  const status = payload.status || (payload.phase === SensitiveApprovalPhase.Pending ? SensitiveApprovalStatus.Pending : undefined)
  if (!status && payload.phase !== SensitiveApprovalPhase.Pending) {
    return null
  }

  return {
    ...payload,
    key: buildApprovalBusinessKey(payload),
    status,
    createdAt: now,
    updatedAt: now,
  }
}

export function formatApprovalPendingTarget(ticket: Pick<SensitiveApprovalPendingTicket, 'operationNames' | 'businessType' | 'businessName' | 'businessId'>) {
  return formatApprovalTarget(ticket)
}

export function dispatchSensitiveApprovalChanged(payload: SensitiveApprovalResponse | SensitiveApprovalWebsocketPayload) {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(new CustomEvent(SENSITIVE_APPROVAL_CHANGED_EVENT, { detail: payload }))
}
